/**
 * Content for the cross-page analysis section on / and /ux-auditor/.
 *
 * DATA ONLY — no JSX, no styling decisions. Same split as
 * homeLandingContent.js and sampleReportContent.js, and for the same reason:
 * both host pages are already over the 500-line limit in CLAUDE.md.
 *
 * ── WHERE EVERY VALUE COMES FROM ────────────────────────────────────────────
 *
 * ONE REAL AUDIT: backend/tests/fixtures/roarsinc_multipage_2026_08_31.json,
 * `audit_1512ecaff8a6`, the three-page paid run against roarsinc.com that
 * /sample-report/ already publishes. FINDING below is
 * `cross_page_findings[6]`, transcribed field for field:
 *
 *     relation    "outlier"
 *     dimension   "visualHierarchy"
 *     severity    "major"
 *     focus       "https://www.roarsinc.com/approach/"
 *     title       quoted verbatim as FINDING.title
 *
 * `backend/tests/test_cross_page_section_matches_fixture.py` parses this
 * literal and compares it to the fixture. Re-run the audit or replace the
 * fixture and the build fails rather than leaving a quotation on two marketing
 * pages that the record no longer supports. Same shape as
 * test_sample_report_matches_fixture and test_homepage_report_card_matches_fixture.
 *
 * ── WHY THIS FINDING AND NOT THE OTHER OUTLIER ──────────────────────────────
 *
 * The record carries two `relation: "outlier"` findings. The other is
 * `cross_page_findings[7]`, whose focus is `/` — the popup that fires on load.
 * Both are real and either could be quoted. This one was chosen because its
 * focus page is `/approach/`: a reader meeting the quote on OUR `/` should not
 * have to work out whether "/" names the audited site's homepage or the one
 * they are standing on. The popup finding stays available in the fixture and
 * on /sample-report/.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 *
 * No count of cross-page findings, no "we find N things across your pages", no
 * comparison to any other tool. The record has 8 cross-page findings and that
 * number is real, but it is one audit of one site: publishing it as a figure
 * would read as a rate, and there is no corpus behind a rate. The section makes
 * a capability claim and shows one instance of it, which is what the record
 * actually supports.
 *
 * The evidence and recommendation strings are also not published. They name
 * `/about-us/` and quote the page's own analysis at length, which is detail
 * that belongs in the report a reader clicks through to, not in a marketing
 * section that has to be readable in one pass.
 */

/**
 * The closed set of relations a cross-page finding may assert, from
 * `backend/audit/cross_page.py:62` (`RELATIONS`), with each `desc` transcribed
 * from the definition comment at `cross_page.py:56-60`.
 *
 * ALL FOUR, IN THE SOURCE'S ORDER. Publishing a subset would let the page
 * describe a smaller capability than the code has, and publishing a prettier
 * set is how "Divergent" and "Progressive" got written here on the first pass:
 * plausible names for relations this product does not have. The real ones are
 * `differs` and `degrades`. Lowercase because that is how the code spells them
 * and how they appear in a report.
 *
 * Kept as literals rather than imported: the marketing pages must not depend on
 * a backend module at build time, the same rule DEMO_AUDIT and the sample
 * report already follow. The pin in
 * test_cross_page_section_matches_fixture.py reads BOTH sources.
 */
export const RELATIONS = [
  { name: 'differs', desc: 'the same element is not the same across pages' },
  { name: 'outlier', desc: 'one page behaves unlike the rest' },
  { name: 'consistent', desc: 'something holds everywhere' },
  { name: 'degrades', desc: 'a quality falls off along a path the customer walks' },
];

/**
 * `cross_page_findings[6]`, the fields that are published.
 *
 * `title` is VERBATIM. It is a quotation from a real record on a public page,
 * so it does not get tidied, shortened, or made to scan better. If it reads
 * awkwardly, that is what the audit said.
 */
export const FINDING = {
  relation: 'outlier',
  dimension: 'visualHierarchy',
  dimensionLabel: 'Visual Hierarchy',
  severity: 'major',
  focus: '/approach/',
  title:
    '/approach/ is the only page that entirely lacks an H1, with the first heading being an H2',
};

/** The audit the quotation comes from, for the attribution line. */
export const SOURCE = {
  auditId: 'audit_1512ecaff8a6',
  pageCount: 3,
  reportPath: '/sample-report/',
};

export const HEADING = 'The finding that needs more than one page';

export const LEAD =
  'Every page is scored on its own, then a third pass looks only at what is true between them. A single-page audit cannot produce this finding at any price, because the claim is about which page differs from the others.';

/**
 * The attribution, and the link out.
 *
 * The link text is not SAMPLE_REPORT_LINK_TEXT ('See a real audit report'):
 * that string names the page for a reader who has not met it, and by this point
 * in the section they have. This one says which report, because the quotation
 * above came out of it and the reader's next question is where the rest is.
 */
export const ATTRIBUTION = 'Quoted verbatim from a three-page audit of roarsinc.com.';
export const LINK_TEXT = 'Read the full report';
